import React, { useCallback, useEffect, useRef, useState } from "react"
import useApi from "../../hooks/useApi.js"

const POLL_INTERVAL_MS = 3000
const TIMEOUT_MS = 90000

const STEP_EYEBROW = {
  gathering_data: "dashboard.funnel.generate.step_gathering",
  calling_ai: "dashboard.funnel.generate.step_calling_ai",
  done: "dashboard.funnel.generate.step_done",
  failed: "dashboard.funnel.generate.step_failed"
}

function Spinner() {
  return (
    <span className="relative flex h-2.5 w-2.5 mt-[7px]" aria-hidden>
      <span
        className="absolute inline-flex h-full w-full rounded-full bg-ai opacity-60"
        style={{ animation: "ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite" }}
      />
      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-ai" />
    </span>
  )
}

function GeneratingView({ t, step, elapsedMs }) {
  const seconds = Math.floor(elapsedMs / 1000)
  return (
    <div className="flex items-start gap-4">
      <Spinner />
      <div className="flex-1">
        <p
          className="font-mono text-[10px] text-ai mb-2"
          style={{ letterSpacing: "0.15em" }}
        >
          {t(STEP_EYEBROW[step] || STEP_EYEBROW.gathering_data)}
        </p>
        <h2
          className="text-[22px] font-medium text-ink-primary mb-2"
          style={{ letterSpacing: "-0.03em" }}
        >
          {t("dashboard.funnel.generate.generating_title")}
        </h2>
        <p className="text-sm text-ink-secondary max-w-xl" style={{ lineHeight: 1.55 }}>
          {t("dashboard.funnel.generate.generating_lede")}
        </p>
        <p
          className="mt-5 font-mono text-[11px] text-ink-tertiary"
          style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "0.05em" }}
        >
          {t("dashboard.funnel.generate.elapsed", { seconds })}
        </p>
      </div>
    </div>
  )
}

function ReadyView({ t, proposal, busy, onApprove, onEdit, onDiscard }) {
  return (
    <div className="space-y-5">
      <div>
        <p
          className="font-mono text-[10px] text-ai mb-2"
          style={{ letterSpacing: "0.15em" }}
        >
          {t("dashboard.funnel.generate.ready_eyebrow")}
        </p>
        <h2
          className="text-[22px] font-medium text-ink-primary"
          style={{ letterSpacing: "-0.03em" }}
        >
          {proposal.funnel_name}
        </h2>
        <p className="mt-2 text-sm text-ink-secondary" style={{ lineHeight: 1.55 }}>
          {t("dashboard.funnel.generate.ready_lede", { count: proposal.steps.length })}
        </p>
      </div>

      <ol className="space-y-2">
        {proposal.steps.map((step, idx) => (
          <li
            key={idx}
            className="flex items-baseline gap-3 bg-surface-page border border-rule rounded-card px-4 py-3"
          >
            <span
              className="font-mono text-[11px] text-ink-tertiary w-6 text-right"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {String(idx).padStart(2, "0")}
            </span>
            <span className="text-[14px] text-ink-primary flex-1">{step.name}</span>
            <code
              className="font-mono text-[12px] text-ink-faint"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {step.url_pattern}
            </code>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap gap-3 items-center pt-2">
        <button
          type="button"
          onClick={onApprove}
          disabled={busy}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-60"
        >
          {busy === "approve"
            ? t("dashboard.funnel.generate.approving")
            : t("dashboard.funnel.generate.approve")}
        </button>
        <button
          type="button"
          onClick={onEdit}
          disabled={busy}
          className="bg-surface-card border border-rule rounded-button px-4 py-2 text-[13px] font-medium text-ink-primary disabled:opacity-60"
        >
          {t("dashboard.funnel.generate.edit")}
        </button>
        <button
          type="button"
          onClick={onDiscard}
          disabled={busy}
          className="ml-auto text-[13px] text-ink-tertiary hover:text-failure disabled:opacity-60"
        >
          {t("dashboard.funnel.generate.discard")}
        </button>
      </div>
    </div>
  )
}

function EditingView({ t, initialProposal, busy, onApprove, onBack }) {
  const [name, setName] = useState(initialProposal.funnel_name)
  const [steps, setSteps] = useState(initialProposal.steps)

  const updateStep = (idx, patch) =>
    setSteps(steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)))
  const removeStep = (idx) => setSteps(steps.filter((_, i) => i !== idx))
  const addStep = () => setSteps([ ...steps, { name: "", url_pattern: "" } ])

  const canApprove =
    !busy &&
    name.trim().length > 0 &&
    steps.length > 0 &&
    steps.every((s) => (s.name || "").trim() && (s.url_pattern || "").trim())

  return (
    <div className="space-y-5">
      <div>
        <p
          className="font-mono text-[10px] text-ai mb-2"
          style={{ letterSpacing: "0.15em" }}
        >
          {t("dashboard.funnel.generate.editing_eyebrow")}
        </p>
        <h2
          className="text-[22px] font-medium text-ink-primary"
          style={{ letterSpacing: "-0.03em" }}
        >
          {t("dashboard.funnel.generate.editing_title")}
        </h2>
      </div>

      <label className="block">
        <span
          className="font-mono text-[10px] text-ink-tertiary"
          style={{ letterSpacing: "0.10em" }}
        >
          {t("dashboard.funnel.funnel_name_label").toUpperCase()}
        </span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
          className="mt-1 w-full bg-surface-page border border-rule rounded-card px-3 py-2 text-[14px] text-ink-primary disabled:opacity-60"
        />
      </label>

      <ol className="space-y-2">
        {steps.map((step, idx) => (
          <li
            key={idx}
            className="flex gap-2 items-center bg-surface-page border border-rule rounded-card px-4 py-3"
          >
            <span
              className="font-mono text-[11px] text-ink-tertiary w-6 text-right"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {String(idx).padStart(2, "0")}
            </span>
            <input
              type="text"
              value={step.name || ""}
              onChange={(e) => updateStep(idx, { name: e.target.value })}
              placeholder={t("dashboard.funnel.step_name_placeholder")}
              disabled={busy}
              className="flex-1 bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary disabled:opacity-60"
            />
            <input
              type="text"
              value={step.url_pattern || ""}
              onChange={(e) => updateStep(idx, { url_pattern: e.target.value })}
              placeholder={t("dashboard.funnel.step_pattern_placeholder")}
              disabled={busy}
              className="flex-[1.5] bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] font-mono text-ink-primary disabled:opacity-60"
              style={{ fontVariantNumeric: "tabular-nums" }}
            />
            <button
              type="button"
              onClick={() => removeStep(idx)}
              disabled={busy}
              className="text-[12px] text-ink-tertiary hover:text-failure disabled:opacity-60"
            >
              {t("dashboard.funnel.step_remove")}
            </button>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap gap-3 items-center pt-2">
        <button
          type="button"
          onClick={addStep}
          disabled={busy}
          className="text-[13px] text-ai hover:underline disabled:opacity-60"
        >
          + {t("dashboard.funnel.step_add")}
        </button>
        <span className="flex-1" />
        <button
          type="button"
          onClick={onBack}
          disabled={busy}
          className="text-[13px] text-ink-tertiary hover:text-ink-primary disabled:opacity-60"
        >
          {t("dashboard.funnel.generate.back")}
        </button>
        <button
          type="button"
          onClick={() => onApprove({ funnel_name: name.trim(), steps })}
          disabled={!canApprove}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-60"
        >
          {busy === "approve"
            ? t("dashboard.funnel.generate.approving")
            : t("dashboard.funnel.generate.approve")}
        </button>
      </div>
    </div>
  )
}

function FailedView({ t, message, onRetry, onClose }) {
  return (
    <div className="space-y-4">
      <p
        className="font-mono text-[10px] text-failure"
        style={{ letterSpacing: "0.15em" }}
      >
        {t("dashboard.funnel.generate.failed_eyebrow")}
      </p>
      <h2
        className="text-[22px] font-medium text-ink-primary"
        style={{ letterSpacing: "-0.03em" }}
      >
        {t("dashboard.funnel.generate.failed_title")}
      </h2>
      {message && (
        <p
          className="font-mono text-[12px] text-ink-tertiary"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {message}
        </p>
      )}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onRetry}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium"
        >
          {t("dashboard.funnel.generate.retry")}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-[13px] text-ink-tertiary hover:text-ink-primary"
        >
          {t("dashboard.funnel.generate.close")}
        </button>
      </div>
    </div>
  )
}

// Four view states: generating | ready | editing | failed.
// Transitions:
//   mount → POST /generate (unless status already has a proposal/failure) → poll /generate_status
//   step=done → ready → (Edit → editing) → Approve → PUT /funnel + DELETE /generate_status → close
//   step=failed → failed → Retry → back to generating
//   Discard/close → DELETE /generate_status → close
export default function GenerateFunnelModal({ t, onClose, onApproved }) {
  const api = useApi()
  const [view, setView] = useState("generating")
  const [step, setStep] = useState("gathering_data")
  const [proposal, setProposal] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(null)
  const [elapsedMs, setElapsedMs] = useState(0)

  const startedAtRef = useRef(Date.now())

  const resetAndStart = useCallback(async () => {
    setError(null)
    setProposal(null)
    setStep("gathering_data")
    setView("generating")
    startedAtRef.current = Date.now()
    setElapsedMs(0)
    try {
      await api.post("/api/v1/shop/funnel/generate")
    } catch (err) {
      setError(err.message)
      setView("failed")
    }
  }, [ api ])

  // On mount: check current status. If generation already finished earlier
  // (owner closed the tab, came back), skip straight to the ready/failed
  // view. Otherwise kick off a fresh generation.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const current = await api.get("/api/v1/shop/funnel/generate_status")
        if (cancelled) return
        if (current && current.type === "funnel" && current.step === "done" && current.proposal) {
          setProposal({
            funnel_name: current.funnel_name || current.proposal.funnel_name || t("dashboard.funnel.generate.default_name"),
            steps: current.proposal.steps || current.proposal
          })
          setView("ready")
        } else if (current && current.type === "funnel" && current.step === "failed") {
          setError(current.error)
          setView("failed")
        } else {
          await resetAndStart()
        }
      } catch (err) {
        if (!cancelled) setError(err.message)
        if (!cancelled) setView("failed")
      }
    })()
    return () => { cancelled = true }
  }, [ api, resetAndStart, t ])

  // Poll /generate_status while generating. Stop on done/failed/timeout.
  useEffect(() => {
    if (view !== "generating") return

    const tick = setInterval(async () => {
      const elapsed = Date.now() - startedAtRef.current
      setElapsedMs(elapsed)
      if (elapsed >= TIMEOUT_MS) {
        setError(t("dashboard.funnel.generate.timeout_message"))
        setView("failed")
        return
      }
      try {
        const status = await api.get("/api/v1/shop/funnel/generate_status")
        if (!status || !status.step) return
        if (status.step === "done" && status.proposal) {
          setProposal({
            funnel_name: status.proposal.funnel_name || t("dashboard.funnel.generate.default_name"),
            steps: status.proposal.steps || []
          })
          setView("ready")
        } else if (status.step === "failed") {
          setError(status.error)
          setView("failed")
        } else {
          setStep(status.step)
        }
      } catch (err) {
        // transient polling error — keep trying until timeout
      }
    }, POLL_INTERVAL_MS)

    return () => clearInterval(tick)
  }, [ view, api, t ])

  const approve = async (overrides = null) => {
    const payload = overrides || proposal
    if (!payload) return
    setBusy("approve")
    setError(null)
    try {
      await api.put("/api/v1/shop/funnel", {
        funnel: {
          name: payload.funnel_name,
          active: true,
          steps: payload.steps
        }
      })
      await api.destroy("/api/v1/shop/funnel/generate_status")
      if (onApproved) await onApproved()
      onClose()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(null)
    }
  }

  const discard = async () => {
    setBusy("discard")
    try {
      await api.destroy("/api/v1/shop/funnel/generate_status")
    } catch (err) {
      // not fatal — modal closes anyway
    } finally {
      setBusy(null)
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(15, 23, 42, 0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) discard() }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-surface-card border border-rule rounded-card-lg w-full max-w-2xl px-7 py-7 max-h-[85vh] overflow-y-auto"
      >
        {view === "generating" && (
          <GeneratingView t={t} step={step} elapsedMs={elapsedMs} />
        )}
        {view === "ready" && proposal && (
          <ReadyView
            t={t}
            proposal={proposal}
            busy={busy}
            onApprove={() => approve()}
            onEdit={() => setView("editing")}
            onDiscard={discard}
          />
        )}
        {view === "editing" && proposal && (
          <EditingView
            t={t}
            initialProposal={proposal}
            busy={busy}
            onApprove={approve}
            onBack={() => setView("ready")}
          />
        )}
        {view === "failed" && (
          <FailedView
            t={t}
            message={error}
            onRetry={resetAndStart}
            onClose={discard}
          />
        )}
      </div>
    </div>
  )
}
